import React from 'react';
import { i18n } from 'utils/setupTranslations';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { RepackBySlugOrGuid } from 'apollo/repack/repack.gql';
import { getSanitizeSlug } from 'helpers/getSanitizeSlug';
import {
    RepackBySlugOrGuidQuery,
    RepackBySlugOrGuidQueryVariables,
} from 'types/generated/graphql';
import { DynamicMetadata } from '../DynamicMetadata/DynamicMetadata';
import { useGetRePackSeoFallback } from './hooks/useGetRePackSeoFallback';

const hasCode = (item: unknown): item is { code: string } =>
    !!item && typeof (item as { code: string }).code === 'string';

const toLowerCaseLocalizeCode = (item: { code: string }) =>
    i18n._(item.code).toLowerCase();

export const useRePackDetails = () => {
    const { slug } = useParams<{ slug: string }>();
    const { data } = useQuery<
        RepackBySlugOrGuidQuery,
        RepackBySlugOrGuidQueryVariables
    >(RepackBySlugOrGuid, {
        skip: !getSanitizeSlug(slug),
        variables: { repackSlugOrGuid: getSanitizeSlug(slug) },
    });

    const {
        artworkUrl,
        name = '',
        brandtype,
        genres,
        instrument: repackInstrument,
    } = data?.repack || {};

    const i18nGenres =
        genres?.filter(hasCode)?.map(toLowerCaseLocalizeCode) ?? [];

    const firstGenre = i18nGenres[0] ?? undefined;
    const secondGenre = i18nGenres[1] ?? undefined;

    const instrument = hasCode(repackInstrument)
        ? toLowerCaseLocalizeCode(repackInstrument)
        : undefined;

    const { title, description } = useGetRePackSeoFallback({
        brandtypeSlug: brandtype?.slug,
        repackName: name,
        firstGenre,
        secondGenre,
        instrument,
    });

    if (!data?.repack?.name) {
        return {};
    }

    return {
        title,
        description,
        imageUrl: artworkUrl,
    };
};

export const RePackDetails = () => {
    const { title, description, imageUrl } = useRePackDetails();

    if (!title || !description) {
        return null;
    }

    return (
        <DynamicMetadata
            imageUrl={imageUrl}
            titleFallback={title}
            descriptionFallback={description}
        />
    );
};
